import { Info } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';

import {
  MARGIN_X,
  MARGIN_Y,
  PADDING_X,
  PADDING_Y,
  COLOR_SCALE,
  SIZE_SCALE,
  Z_INDEX,
  RADIUS_SCALE,
  SHADOW_SCALE,
  BORDER_WIDTH,
  FONT_WEIGHTS,
  TYPE_SCALE,
  TYPE_LEADING,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';

interface MedicalTermTooltipProps {
  term: string;
  definition: string;
  children: React.ReactNode;
  inline?: boolean;
}

/**
 * MedicalTermTooltip Component
 *
 * Displays helpful tooltips for medical terminology
 * - Hover to see definition
 * - Click/tap on mobile to show
 * - Positioned intelligently to avoid overflow
 * - Accessible with ARIA labels
 */
function MedicalTermTooltip({
  term,
  definition,
  children,
  inline = false,
}: MedicalTermTooltipProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState('bottom');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      // Check if tooltip would overflow viewport
      const rect = triggerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // If close to bottom, show above; otherwise show below
      if (rect.bottom + 150 > windowHeight) {
        setPosition('top');
      } else {
        setPosition('bottom');
      }
    }
  }, [isVisible]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  // If no definition provided, just render children
  if (!definition) {
    return <>{children}</>;
  }

  const tooltipContent = (
    <>
      {children}
      <button
        ref={triggerRef}
        className={`inline-flex items-center align-middle ${MARGIN_X['1']} ${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE.foreground} transition-colors`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        aria-label={`${String(t('navigation_extended', 'more_info', 'More information about'))} ${term}`}
        type="button"
      >
        <Info className={`${SIZE_SCALE['3.5']}`} aria-hidden="true" />
      </button>
    </>
  );

  return (
    <span className={inline ? 'inline-flex items-center' : 'relative inline-block'}>
      {tooltipContent}

      {/* Tooltip */}
      {isVisible && (
        <>
          {/* Backdrop for mobile - click to dismiss */}
          <div
            className={`lg:hidden fixed inset-0 ${Z_INDEX.dropdown}`}
            onClick={() => setIsVisible(false)}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setIsVisible(false);
              }
            }}
            tabIndex={-1}
            role="button"
            aria-label="Close tooltip"
          />

          <div
            ref={tooltipRef}
            className={`absolute ${position === 'top' ? `bottom-full ${MARGIN_Y['2']}` : `top-full ${MARGIN_Y['2']}`} left-1/2 -translate-x-1/2 ${Z_INDEX.dropdown} ${SIZE_SCALE.w64} sm:w-72`}
            onClick={e => e.stopPropagation()}
            role="presentation"
          >
            <div
              className={`${COLOR_SCALE['tooltip-bg']} ${COLOR_SCALE['tooltip-text']} ${TYPE_SCALE['2xs']} ${RADIUS_SCALE.lg} ${SHADOW_SCALE['2xl']} overflow-hidden border ${COLOR_SCALE['tooltip-border']}`}
            >
              {/* Header */}
              <div
                className={`${PADDING_X['3']} ${PADDING_Y['2']} ${COLOR_SCALE['tooltip-header-bg']} ${BORDER_WIDTH.thin}-b ${COLOR_SCALE['tooltip-border']}`}
              >
                <div
                  className={`${FONT_WEIGHTS.semibold} ${COLOR_SCALE['tooltip-text']} break-words ${TYPE_LEADING.snug}`}
                >
                  {term}
                </div>
              </div>

              {/* Content */}
              <div className={`${PADDING_X['3']} ${PADDING_Y['2.5']}`}>
                <p
                  className={`${TYPE_LEADING.relaxed} ${COLOR_SCALE['tooltip-text-muted']} break-words`}
                >
                  {definition}
                </p>
              </div>

              {/* Arrow */}
              <div
                className={`absolute left-1/2 -translate-x-1/2 ${SIZE_SCALE['2']} ${COLOR_SCALE['tooltip-bg']} ${COLOR_SCALE['tooltip-border']} transform rotate-45 ${
                  position === 'top' ? '-bottom-1 border-b border-r' : '-top-1 border-t border-l'
                }`}
              />
            </div>
          </div>
        </>
      )}
    </span>
  );
}

const MedicalTermTooltipWithMonitor = withMemoryLeakMonitor(MedicalTermTooltip, {
  componentName: 'MedicalTermTooltip',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
});

export default MedicalTermTooltipWithMonitor;
